import { useCallback, useRef } from 'react';
import { nonNull } from '@helpers/non-null';

export interface ScreenCaptureController {
  snapshot: (signal: AbortSignal) => Promise<{
    blob: Blob;
    width: number;
    height: number;
    sourceWidth: number;
    sourceHeight: number;
  }>;
  onStop: (callback: () => void) => () => void;
}

export function useScreenCapture() {
  const initialized = useRef<false | Promise<ScreenCaptureController>>(false);
  const initialize = useCallback(
    (cropTargetEl: HTMLElement, initSignal: AbortSignal) => {
      if (initialized.current) return initialized.current;
      const streamPromise = navigator.mediaDevices.getDisplayMedia({
        audio: false,
        video: {
          displaySurface: 'browser',
          cursor: 'never',
          frameRate: { ideal: 30 },
          contentHint: 'text',
          resizeMode: 'none'
        },
        selfBrowserSurface: 'include',
        preferCurrentTab: true,
        monitorTypeSurfaces: 'exclude',
        surfaceSwitching: 'exclude',
        systemAudio: 'exclude',
        windowAudio: 'exclude'
      } as DisplayMediaStreamOptions);
      let _stream: MediaStream | null = null;
      const promise = streamPromise
        .then(async (stream) => {
          initSignal.throwIfAborted();
          const [track] = stream.getVideoTracks();
          let cropped = false;
          if (typeof (window as any).CropTarget !== 'undefined') {
            const cropTarget = await (window as any).CropTarget.fromElement(
              cropTargetEl
            );
            initSignal.throwIfAborted();
            await (track as any).cropTo(cropTarget);
            initSignal.throwIfAborted();
            cropped = true;
          }
          return { stream, track, cropped };
        })
        .then(({ stream, track, cropped }) => {
          initSignal.throwIfAborted();
          _stream = stream;
          const video = document.createElement('video');
          video.muted = true;
          video.playsInline = true;
          video.srcObject = stream;
          video.addEventListener('loadedmetadata', () => {
            video.play();
          });

          const initialDelayPromise = new Promise<void>((resolve) => {
            setTimeout(resolve, 2500);
          });

          const grabFrame = async (
            signal: AbortSignal,
            repaintTime: number
          ) => {
            const abortPromise = new Promise<void>((resolve) => {
              signal.addEventListener('abort', () => resolve());
            });
            const videoFramePromise = new Promise<void>((resolve) => {
              // We wait for N frames to ensure that the video has fully updated and any UI elements have been hidden.
              const flushFrames = 3;

              // ---- LEVEL 1: requestVideoFrameCallback (Modern Way) ----
              if (typeof video.requestVideoFrameCallback === 'function') {
                let framesChecked = 0;
                const checkFrame: VideoFrameRequestCallback = (_, metadata) => {
                  signal.throwIfAborted();
                  framesChecked++;
                  // If we found a frame captured after our local repaint, or we've flushed N frames
                  if (
                    metadata.captureTime &&
                    metadata.captureTime > repaintTime
                  ) {
                    resolve();
                  } else if (framesChecked >= flushFrames) {
                    resolve();
                  } else {
                    video.requestVideoFrameCallback(checkFrame);
                  }
                };
                video.requestVideoFrameCallback(checkFrame);
                return;
              }

              // ---- LEVEL 2: getVideoPlaybackQuality (Legacy Fallback) ----
              const initialFrames =
                video.getVideoPlaybackQuality().totalVideoFrames;
              const targetFrames = initialFrames + flushFrames;
              const checkFrame = () => {
                signal.throwIfAborted();
                const currentFrames =
                  video.getVideoPlaybackQuality().totalVideoFrames;
                if (currentFrames >= targetFrames) {
                  resolve();
                } else {
                  requestAnimationFrame(checkFrame);
                }
              };
              requestAnimationFrame(checkFrame);
            });
            await Promise.race([videoFramePromise, abortPromise]);
            signal.throwIfAborted();
            return video;
          };

          const snapshot = async (signal: AbortSignal, repaintTime: number) => {
            const abortController = new AbortController();
            initSignal.addEventListener('abort', () => {
              abortController.abort();
            });
            signal.addEventListener('abort', () => {
              abortController.abort();
            });
            signal = abortController.signal;
            const canvas = document.createElement('canvas');
            const ctx = nonNull(canvas.getContext('2d'));
            ctx.imageSmoothingEnabled = false;
            const frame = await grabFrame(signal, repaintTime);
            const frameWidth = frame.videoWidth;
            const frameHeight = frame.videoHeight;
            const sourceRect = cropTargetEl.getBoundingClientRect();
            if (cropped) {
              canvas.width = frameWidth;
              canvas.height = frameHeight;
              ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
            } else {
              canvas.width = sourceRect.width;
              canvas.height = sourceRect.height;
              const scaleX = frameWidth / window.innerWidth;
              const scaleY = frameHeight / window.innerHeight;
              const sx = sourceRect.left * scaleX;
              const sy = sourceRect.top * scaleY;
              const sw = sourceRect.width * scaleX;
              const sh = sourceRect.height * scaleY;
              ctx.drawImage(
                frame,
                sx,
                sy,
                sw,
                sh,
                0,
                0,
                canvas.width,
                canvas.height
              );
            }
            return new Promise<{
              blob: Blob;
              width: number;
              height: number;
              sourceWidth: number;
              sourceHeight: number;
            }>((resolve, reject) => {
              canvas.toBlob(
                (blob) => {
                  try {
                    signal.throwIfAborted();
                  } catch (err) {
                    reject(err);
                    return;
                  }
                  if (blob) {
                    resolve({
                      blob,
                      width: canvas.width,
                      height: canvas.height,
                      sourceWidth: sourceRect.width,
                      sourceHeight: sourceRect.height
                    });
                  } else {
                    reject(new Error('Failed to create blob'));
                  }
                },
                'image/png',
                1
              );
            });
          };

          const controller: ScreenCaptureController = {
            onStop: (callback) => {
              track.addEventListener('ended', callback);
              return () => {
                track.removeEventListener('ended', callback);
              };
            },
            snapshot: async (signal) => {
              const abortPromise = new Promise<void>((resolve) => {
                signal.addEventListener('abort', () => resolve());
              });
              await Promise.race([abortPromise, initialDelayPromise]);
              signal.throwIfAborted();

              // Before taking a snapshot, we need to hide any UI elements that might be overlaid on the page
              // and then wait for the next video frame to be delivered.
              // This ensures that we capture a clean snapshot of the page without any UI artifacts.
              document.body.classList.add(
                'media_viewer_website_snapshot--in-progress'
              );
              try {
                // Force a layout reflow to ensure that the class is applied before we take the snapshot.
                // Reading this property forces the browser to instantly calculate styles
                // and layouts for the body and all of its descendants.
                const _ = document.body.offsetHeight;
                const repaintPromise = new Promise<void>((resolve) => {
                  requestAnimationFrame(() =>
                    requestAnimationFrame(() => resolve())
                  );
                });
                await Promise.race([abortPromise, repaintPromise]);
                signal.throwIfAborted();
                return await snapshot(signal, performance.now());
              } finally {
                // Remove the class after the snapshot is taken or if the operation is aborted
                document.body.classList.remove(
                  'media_viewer_website_snapshot--in-progress'
                );
              }
            }
          };
          return controller;
        });
      initialized.current = promise;
      initSignal.addEventListener('abort', () => {
        if (initialized.current === promise) {
          initialized.current = false;
        }
        if (_stream) {
          _stream.getTracks().forEach((track) => track.stop());
          _stream = null;
        }
      });
      promise.then((controller) => {
        controller.onStop(() => {
          if (initialized.current === promise) {
            initialized.current = false;
          }
        });
      });
      promise.catch((err) => {
        console.error('Failed to get display media', err);
        if (initialized.current === promise) {
          initialized.current = false;
        }
      });
      return promise;
    },
    []
  );
  return initialize;
}
